import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Snackbar, Typography } from '@barracuda-internal/bds-core-v2';
import Add from '@barracuda-internal/bds-core-v2/dist/Icons/Core/Add';
import { useAppToolchain } from 'src/hooks/useAppToolchain';
import useAuth from 'src/hooks/useAuth';
import IdentityGroups from './Identity/IdentityGroups';
import DescriptionInput from './Description';
import IdentityUsers from './Identity/IdentityUsers';
import ExceptionGroups from './Exception/ExceptionGroups';
import ExceptionUsers from './Exception/ExceptionUsers';
import Classifiers from './Classifiers';
import Authorship from './Authorship';
import Actions from './Actions';
import PolicyName from './PolicyName';
import { Policy } from 'src/types/policyTypes';
import { SnackbarCloseReason } from '@mui/material/Snackbar';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from 'src/redux/store';
import { closeDrawer } from 'src/features/policyengine/policyEngineSlice';
import { createPolicy, updatePolicy, updatePriorities } from 'src/features/policyengine/thunks';

type Anchor = 'top' | 'left' | 'bottom' | 'right';

const defaultErrorText = 'Unexpected Error! Please try again after sometime.';

interface PolicySettingsProps {
	anchor: Anchor;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const PolicySettings = ({ anchor }: PolicySettingsProps): React.ReactElement => {
	const { api } = useAppToolchain();
	const { auth } = useAuth();
	const dispatch = useDispatch<AppDispatch>();

	const [showExceptionList, setShowExceptionList] = useState(false);

	const { selectedPolicyToModify, modifyClicked, policyFeeds } = useSelector(
		(state: RootState) => state.policyEngine
	);

	const [policyName, setPolicyName] = useState(selectedPolicyToModify?.id ? selectedPolicyToModify.name : '');
	const [policyNameError, setPolicyNameError] = useState(false);
	const [defaultPolicyNameError, setDefaultPolicyNameError] = useState(false);
	const [policyDescription, setPolicyDescription] = useState(
		selectedPolicyToModify?.id ? selectedPolicyToModify.description : ''
	);
	const selectedClassifiers = useRef(
		selectedPolicyToModify?.id ? selectedPolicyToModify.conditions.findingTags : []
	);
	const [classifierError, setClassifierError] = useState(false);
	const [hideCreate, setHideCreate] = useState(false);
	const [hideSave, setHideSave] = useState(false);
	const [errorToast, setErrorToast] = React.useState(false);
	const [errorToastMessage, setErrorToastMessage] = React.useState(defaultErrorText);

	const selectedInclusionGroups = useRef(
		selectedPolicyToModify?.id ? selectedPolicyToModify.conditions.identity.inclusion.groups : []
	);
	const selectedInclusionUsers = useRef(
		selectedPolicyToModify?.id ? selectedPolicyToModify.conditions.identity.inclusion.users : []
	);
	const [inclusionError, setInclusionError] = useState(false);

	const selectedExceptGroups = useRef(
		selectedPolicyToModify?.id ? selectedPolicyToModify.conditions.identity.exclusion.groups : []
	);
	const selectedExceptUsers = useRef(
		selectedPolicyToModify?.id ? selectedPolicyToModify.conditions.identity.exclusion.users : []
	);
	const selectedAuthorship = useRef(
		selectedPolicyToModify?.id
			? selectedPolicyToModify.conditions.identity.authorship
			: ['Owner', 'Creator', 'LastModifier']
	);
	const [authorshipError, setAuthorshipError] = useState(false);
	const selectedAction = useRef(selectedPolicyToModify?.id ? selectedPolicyToModify.actions : []);

	const accountId = auth.accountDetails.account.id;

	const handleClickExceptionList = () => {
		setShowExceptionList(true);
	};

	useEffect(() => {
		setShowExceptionList(
			() =>
				(selectedPolicyToModify?.conditions?.identity?.exclusion?.groups.length ||
					selectedPolicyToModify?.conditions?.identity?.exclusion?.users.length)
					? true
					: false
		);
	}, [selectedPolicyToModify]);

	const InputValidation = () => {
		let validData = true;

		if (policyNameError) {
			validData = false;
		}
		if (defaultPolicyNameError) {
			validData = false;
		}
		if (policyName.length === 0) {
			setPolicyNameError(true);
			validData = false;
		}
		if (selectedClassifiers.current.length === 0) {
			setClassifierError(true);
			validData = false;
		}
		if (selectedInclusionUsers.current.length === 0 && selectedInclusionGroups.current.length === 0) {
			setInclusionError(true);
			validData = false;
		}
		if (selectedAuthorship.current.length === 0) {
			setAuthorshipError(true);
			validData = false;
		}
		return validData;
	};

	const handleSave = () => {
		if (!InputValidation()) {
			return;
		}
		setHideSave(true);

		const reqPayload = {
			name: policyName,
			description: policyDescription.length === 0 ? '-' : policyDescription,
			priority: (selectedPolicyToModify as Policy).priority,
			conditions: {
				identity: {
					inclusion: {
						users: selectedInclusionUsers.current,
						groups: selectedInclusionGroups.current,
					},
					exclusion: {
						users: selectedExceptUsers.current,
						groups: selectedExceptGroups.current,
					},
					authorship: selectedAuthorship.current,
				},
				findingTags: selectedClassifiers.current,
			},
			actions: selectedAction.current,
			createdBy: (selectedPolicyToModify as Policy).createdBy,
			disabled: (selectedPolicyToModify as Policy).disabled,
		};

		dispatch(
			updatePolicy({
				api,
				accountId,
				payload: reqPayload,
				policyId: (selectedPolicyToModify as Policy).id,
			})
		)
			.unwrap()
			.catch((error) => {
				console.error('Error on updating the policy:', error);
				setHideSave(false);
			});
	};

	const handleCreate = () => {
		if (!InputValidation()) {
			return;
		}
		setHideCreate(true);

		const reqPayload = {
			name: policyName,
			description: policyDescription.length === 0 ? '-' : policyDescription,
			priority: 1,
			conditions: {
				identity: {
					inclusion: {
						users: selectedInclusionUsers.current,
						groups: selectedInclusionGroups.current,
					},
					exclusion: {
						users: selectedExceptUsers.current,
						groups: selectedExceptGroups.current,
					},
					authorship: selectedAuthorship.current,
				},
				findingTags: selectedClassifiers.current,
			},
			actions: selectedAction.current,
			disabled: true,
		};

		dispatch(createPolicy({ api, accountId, payload: reqPayload }))
			.unwrap()
			.then(() => {
				const priorityChange = policyFeeds.map((feed, index) => ({
					id: feed.id,
					priority: index + 2,
				}));

				if (priorityChange.length > 0) {
					dispatch(updatePriorities({ api, accountId, priorities: priorityChange, skipToast: true }));
				}
			})
			.catch((error) => {
				console.error('Error on creating the policy:', error);
				setHideCreate(false);
			});
	};

	const handleToastClose = (event?: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
		if (reason === 'clickaway') {
			return;
		}

		setErrorToast(false);
		setErrorToastMessage(defaultErrorText);
	};

	const handleCloseDrawer = () => {
		dispatch(closeDrawer());
	};

	const rowContainerSx = {
		display: 'flex',
		alignItems: 'center',
		width: '100%',
		margin: '7px 0px',
	};

	const conditionCol1Sx = {
		width: '8%',
		textAlign: 'center',
		color: 'gray',
	};

	const conditionCol2Sx = {
		width: '15%',
		textAlign: 'center',
		fontWeight: 500,
	};

	const conditionCol3Sx = {
		width: '4%',
		textAlign: 'center',
	};

	return (
		<Box
			sx={{
				width: 1050,
				minHeight: '100vh',
				display: 'flex',
				flexDirection: 'column',
				overflowX: 'auto',
				background: '#f7f7f8',
			}}
			role="presentation"
		>
			<Box
				sx={{
					margin: '24px',
					display: 'flex',
					flexDirection: 'column',
					background: 'white',
					padding: '20px',
					marginBottom: '20px',
				}}
			>
				<PolicyName
					limit={60}
					labelTxt={'Policy name'}
					variant={'small'}
					value={policyName}
					setValue={setPolicyName}
					error={policyNameError}
					setError={setPolicyNameError}
					isDefault={selectedPolicyToModify?.isDefault}
					defaultError={defaultPolicyNameError}
					setDefaultError={setDefaultPolicyNameError}
				/>

				<DescriptionInput
					limit={120}
					labelTxt={'Description (optional)'}
					variant={'medium'}
					value={policyDescription}
					setValue={setPolicyDescription}
				/>
			</Box>

			<Box
				sx={{
					margin: '5px 24px',
					display: 'flex',
					flexDirection: 'column',
					background: 'white',
					padding: '20px',
				}}
			>
				<Typography variant="caption" sx={{ fontSize: '10px', fontWeight: 500, color: 'gray' }}>
					Condition
				</Typography>
				<Box sx={rowContainerSx}>
					<Typography component="span" sx={conditionCol1Sx}>IF</Typography>
					<Typography component="span" sx={conditionCol2Sx}>Classifier</Typography>
					<Typography component="span" sx={conditionCol3Sx}>is</Typography>
					<Box sx={{ flex: 1 }}>
						<Classifiers
							selectedClassifiers={selectedClassifiers}
							error={classifierError}
							setError={setClassifierError}
						/>
					</Box>
				</Box>
				<Box sx={rowContainerSx}>
					<Typography component="span" sx={conditionCol1Sx}>AND</Typography>
					<Typography component="span" sx={conditionCol2Sx}>Identity</Typography>
					<Typography component="span" sx={conditionCol3Sx}>is</Typography>
					<Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: '10px' }}>
						<Box sx={{ flex: 1 }}>
							<IdentityGroups
								selectedGroups={selectedInclusionGroups}
								setError={setInclusionError}
								selectedUsers={selectedInclusionUsers}
							/>
						</Box>
						<Box sx={{ flex: 1 }}>
							<IdentityUsers
								selectedUsers={selectedInclusionUsers}
								setError={setInclusionError}
								selectedGroups={selectedInclusionGroups}
							/>
						</Box>
					</Box>
				</Box>
				{inclusionError && (
					<Typography sx={{ color: '#d32f2f', alignSelf: 'center' }}>
						Atleast a group or user must be selected on Identity
					</Typography>
				)}
				<Box sx={rowContainerSx}>
					<Button
						bdsType="interactiveSubtle"
						sx={{ width: '27%' }}
						onClick={handleClickExceptionList}
					>
						<Add fontSize="small" sx={{ marginRight: '8px' }} /> Except
					</Button>
					{showExceptionList && (
						<Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: '10px' }}>
							<Box sx={{ flex: 1 }}>
								<ExceptionGroups selectedGroups={selectedExceptGroups} />
							</Box>
							<Box sx={{ flex: 1 }}>
								<ExceptionUsers selectedUsers={selectedExceptUsers} />
							</Box>
						</Box>
					)}
				</Box>
				<Box sx={rowContainerSx}>
					<Typography component="span" sx={conditionCol1Sx}>AND</Typography>
					<Typography component="span" sx={conditionCol2Sx}>Authorship of file</Typography>
					<Typography component="span" sx={conditionCol3Sx}>is</Typography>
					<Box sx={{ flex: 1 }}>
						<Authorship
							selectedAuthorship={selectedAuthorship}
							error={authorshipError}
							setError={setAuthorshipError}
						/>
					</Box>
				</Box>
			</Box>

			<Box
				sx={{
					margin: '24px',
					display: 'flex',
					flexDirection: 'column',
					background: 'white',
					padding: '20px',
				}}
			>
				<Typography variant="caption" sx={{ fontSize: '10px', fontWeight: 500, color: 'gray' }}>
					Action
				</Typography>
				<Box sx={rowContainerSx}>
					<Typography component="span" sx={conditionCol1Sx}>THEN</Typography>
					<Box sx={{ width: '40%' }}>
						<Actions selectedAction={selectedAction} />
					</Box>
				</Box>
			</Box>

			<Box
				sx={{
					marginTop: 'auto',
					background: 'white',
					display: 'flex',
					padding: '25px',
				}}
			>
				<Snackbar
					open={errorToast}
					autoHideDuration={3000}
					onClose={handleToastClose}
					anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
				>
					<Alert severity="error" variant="standard">
						{errorToastMessage}
					</Alert>
				</Snackbar>
				<Button
					bdsType="interactiveSubtle"
					sx={{ marginLeft: 'auto', marginRight: '25px' }}
					onClick={handleCloseDrawer}
				>
					Cancel
				</Button>
				{!modifyClicked && (
					<Button
						sx={{ marginRight: '75px', visibility: hideCreate ? 'hidden' : 'visible' }}
						onClick={handleCreate}
					>
						Create
					</Button>
				)}
				{modifyClicked && (
					<Button
						sx={{ marginRight: '75px', visibility: hideSave ? 'hidden' : 'visible' }}
						onClick={handleSave}
					>
						Save
					</Button>
				)}
			</Box>
		</Box>
	);
};
